// API service for backend communication
import { supabase } from './supabase';

// Empty string means same origin (relative paths)
const API_URL = import.meta.env.VITE_API_URL === '' ? '' : (import.meta.env.VITE_API_URL || 'http://localhost:3001');

/**
 * Get the current auth token from Supabase session
 */
const getAuthToken = async (): Promise<string | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

/**
 * Create headers with auth token
 */
const getAuthHeaders = async (): Promise<HeadersInit> => {
  const token = await getAuthToken();
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface ApiUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  user_metadata: any;
  app_metadata: any;
  profile: any;
  company: any;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

// Get all users with profile and company data
export const getUsers = async (): Promise<ApiUser[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/users`, { headers });
    const result: ApiResponse<ApiUser[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch users');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

// Get user by ID
export const getUserById = async (id: string): Promise<ApiUser> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/users/${id}`, { headers });
    const result: ApiResponse<ApiUser> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch user');
    }
    
    if (!result.data) {
      throw new Error('User not found');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching user:', error);
    throw error;
  }
};

// Update user
export const updateUser = async (id: string, updates: any): Promise<ApiUser> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates),
    });
    
    const result: ApiResponse<ApiUser> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to update user');
    }
    
    if (!result.data) {
      throw new Error('No data returned');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

// Delete user
export const deleteUser = async (id: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/users/${id}`, {
      method: 'DELETE',
      headers,
    });
    
    const result: ApiResponse<null> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete user');
    }
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

// Get database statistics
export const getStats = async (): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/stats`, { headers });
    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch stats');
    }
    
    return result.data || {};
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
};

// Health check
export const healthCheck = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/api/health`);
    const result = await response.json();
    return result.status === 'OK';
  } catch (error) {
    console.error('Health check failed:', error);
    return false;
  }
};

// ==========================================
// ADMIN USER MANAGEMENT API
// ==========================================

export interface AdminUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at?: string;
  email_confirmed_at?: string;
  user_metadata?: {
    full_name?: string;
    first_name?: string;
    last_name?: string;
  };
  app_metadata?: {
    role?: string;
  };
}

// Get all admin users
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/admin-users`, { headers });
    const result: ApiResponse<AdminUser[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch admin users');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw error;
  }
};

// Create new admin user
export const createAdminUser = async (email: string, password: string): Promise<AdminUser> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/admin-users`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email, password }),
    });
    
    const result: ApiResponse<AdminUser> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to create admin user');
    }
    
    if (!result.data) {
      throw new Error('No data returned');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
};

// Delete admin user
export const deleteAdminUser = async (id: string): Promise<void> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/admin-users/${id}`, {
      method: 'DELETE',
      headers,
    });
    
    const result: ApiResponse<null> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to delete admin user');
    }
  } catch (error) {
    console.error('Error deleting admin user:', error);
    throw error;
  }
};

// ==========================================
// COMPANY API FUNCTIONS
// ==========================================

// Get company by ID
export const getCompanyById = async (id: string): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${id}`, {
      method: 'GET',
      headers,
    });
    
    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to fetch company');
    }
    
    if (!result.data) {
      throw new Error('No data returned');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error fetching company:', error);
    throw error;
  }
};

// Get classifications by IDs
export const getClassificationsByIds = async (ids: (number | string)[]): Promise<any[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/classifications/by-ids`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ids }),
    });
    
    const result: ApiResponse<any[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to fetch classifications');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching classifications:', error);
    throw error;
  }
};

// Get messages for a company
export const getCompanyMessages = async (companyId: string, limit: number = 10): Promise<any[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${companyId}/messages?limit=${limit}`, {
      method: 'GET',
      headers,
    });
    
    const result: ApiResponse<any[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to fetch messages');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw error;
  }
};

// Update company
export const updateCompany = async (id: string, updateData: any): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updateData),
    });
    
    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to update company');
    }
    
    if (!result.data) {
      throw new Error('No data returned');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error updating company:', error);
    throw error;
  }
};

// Link user to company
export const linkUserToCompany = async (companyId: string, userId: string): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${companyId}/link-user`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ userId }),
    });
    
    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to link user');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error linking user to company:', error);
    throw error;
  }
};

// Unlink user from company
export const unlinkUserFromCompany = async (userId: string): Promise<any> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/users/${userId}/unlink-company`, {
      method: 'POST',
      headers,
    });
    
    const result: ApiResponse<any> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to unlink user');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error unlinking user from company:', error);
    throw error;
  }
};

// Get auth users for a specific company
export const getCompanyAuthUsers = async (companyId: string | number): Promise<ApiUser[]> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${companyId}/users`, { headers });
    const result: ApiResponse<ApiUser[]> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch company auth users');
    }
    
    return result.data || [];
  } catch (error) {
    console.error('Error fetching company auth users:', error);
    throw error;
  }
};

// Create auth user for a company
export const createAuthUserForCompany = async (companyId: string, password?: string): Promise<{ user: any; tempPassword: string }> => {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/companies/${companyId}/create-auth-user`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ password }),
    });
    
    const result: ApiResponse<{ user: any; tempPassword: string }> = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || result.message || 'Failed to create auth user for company');
    }
    
    if (!result.data) {
      throw new Error('No data returned');
    }
    
    return result.data;
  } catch (error) {
    console.error('Error creating auth user for company:', error);
    throw error;
  }
};

// ==========================================
// Company accounts: one PCU ID login per company + email accounts under it
// ==========================================

export interface MemberAccount {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  disabled: boolean;
  must_change_password: boolean;
}

export interface CompanyAccount {
  id: string;
  pcu_id: string;
  name: string;
  email: string | null;
  city_id: number | null;
  cities: any;
  /** The company account (PCU ID login), if created. */
  account: MemberAccount | null;
  /** Email accounts linked to the company, each with its own password. */
  users: MemberAccount[];
}

const accountsRequest = async <T>(path: string, method = 'GET', body?: any): Promise<T> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_URL}/api/accounts${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const result: ApiResponse<T> = await response.json();
  if (!result.success) throw new Error(result.message || result.error || 'Request failed');
  return result.data as T;
};

export const getCompanyAccounts = () => accountsRequest<CompanyAccount[]>('');

export const createCompanyAccount = (companyId: string, password: string) =>
  accountsRequest<MemberAccount>(`/${companyId}`, 'POST', { password });

export const addEmailAccount = (companyId: string, email: string, password: string) =>
  accountsRequest<MemberAccount>(`/${companyId}/users`, 'POST', { email, password });

export const setAccountPassword = (userId: string, password: string) =>
  accountsRequest<void>(`/users/${userId}/password`, 'PUT', { password });

export const setAccountEmail = (userId: string, email: string) =>
  accountsRequest<void>(`/users/${userId}/email`, 'PUT', { email });

export const setAccountDisabled = (userId: string, disabled: boolean) =>
  accountsRequest<void>(`/users/${userId}/status`, 'PUT', { disabled });

export interface DefaultPasswordResult {
  companyId: string;
  pcu_id: string;
  email: string;
  ok: boolean;
  created?: boolean;
  error?: string;
}

/** Set membership-no + suffix as the password for every account of the
 *  selected companies (creating missing company accounts). */
export const setDefaultPasswords = (companyIds: string[]) =>
  accountsRequest<{ suffix: string; results: DefaultPasswordResult[] }>('/bulk/default-password', 'POST', { companyIds });
